import { DateTime } from 'ts-luxon'

/** Формат локального «настенного» времени без зоны/смещения (как ждёт бэк для `ver_date`). */
const LOCAL_WALL_CLOCK_FORMAT = "yyyy-MM-dd'T'HH:mm:ss"

/**
 * Текущий момент как локальное «настенное» время `yyyy-MM-dd'T'HH:mm:ss`
 * (без `Z` / смещения).
 *
 * Та же форма, что у {@link parseDmyToLocalTimestamp} и InventPassportSaveDateModal —
 * используется для `ver_date` при сохранении.
 */
export function formatLocalWallClockVerDateNow(): string {
  return DateTime.local().startOf('second').toFormat(LOCAL_WALL_CLOCK_FORMAT)
}

/**
 * Разобрать дату `dd.MM.yyyy` и объединить её с текущим локальным временем.
 *
 * @param value строка вида `dd.MM.yyyy`
 * @returns локальный «настенный» таймстамп (с точностью до секунд) или `null`
 */
export function parseDmyToLocalTimestamp(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return null

  const dateOnly = DateTime.fromFormat(trimmed, 'dd.MM.yyyy')
  if (!dateOnly.isValid) return null

  const nowLocal = DateTime.local()
  const withCurrentTime = dateOnly.set({
    hour: nowLocal.hour,
    minute: nowLocal.minute,
    second: nowLocal.second,
    millisecond: 0,
  })

  return withCurrentTime.toFormat(LOCAL_WALL_CLOCK_FORMAT)
}

/**
 * Разобрать дату статуса с бэка и нормализовать для отображения у клиента.
 *
 * - С явной зоной (`Z` / `+hh:mm`): перевести в локальную зону.
 * - Без зоны: считать уже локальным «настенным» таймстампом
 *   (`ver_date` / `status_dates` — naive MSK/local, не UTC).
 *
 * @param raw сырое значение даты с сервера
 * @returns валидный `DateTime` (luxon) либо невалидный `DateTime` при ошибке разбора
 */
export function statusDateToClientLocal(raw: string): DateTime {
  const normalized = String(raw ?? '')
    .trim()
    .replace(' ', 'T')
  if (!normalized) return DateTime.invalid('empty')

  // Не дописываем `Z`: иначе naive `ver_date` трактуется как UTC и на MSK
  // уезжает на +3 при toLocal() (сохранили 13:11 — показали 16:11).
  const dt = DateTime.fromISO(normalized, { setZone: true })
  return dt.isValid ? dt.toLocal() : DateTime.invalid('unsupported')
}
